import { STSClient, AssumeRoleWithWebIdentityCommand } from '@aws-sdk/client-sts'
import type { AwsCredentialIdentity } from '@aws-sdk/types'

export type AdminCredentials = AwsCredentialIdentity & { expiration: Date }

function isNotAdminError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false
  const name = (err as { name?: string }).name
  // Only treat explicit authorization denials as "not an admin." Token
  // validation failures (InvalidIdentityToken, IDPRejectedClaim) and IDP
  // transport failures (IDPCommunicationError) are real problems — let them
  // propagate so the caller can surface them.
  return name === 'AccessDenied' || name === 'AccessDeniedException'
}

export async function tryAssumeAdminRole(
  idToken: string,
  region: string,
  sessionName: string,
): Promise<AdminCredentials | null> {
  const roleArn = import.meta.env.VITE_ADMIN_ROLE_ARN
  if (!roleArn) return null

  const sts = new STSClient({ region })
  try {
    const res = await sts.send(
      new AssumeRoleWithWebIdentityCommand({
        RoleArn: roleArn,
        RoleSessionName: sessionName,
        WebIdentityToken: idToken,
        DurationSeconds: 3600,
      }),
    )
    const c = res.Credentials
    if (!c?.AccessKeyId || !c.SecretAccessKey || !c.SessionToken || !c.Expiration) {
      return null
    }
    return {
      accessKeyId: c.AccessKeyId,
      secretAccessKey: c.SecretAccessKey,
      sessionToken: c.SessionToken,
      expiration: c.Expiration,
    }
  } catch (err) {
    if (isNotAdminError(err)) return null
    console.error('[admin] STS assume-role failed unexpectedly', err)
    throw err
  } finally {
    sts.destroy()
  }
}
